import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Kicker } from '@/components/Kicker'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { PACK_COLORS } from '@/design-system/tokens'
import { emitEvent } from '@/lib/emitEvent'
import {
  seedDueAt,
  seedNeedsFromAnswers,
  seedPreview,
  type CareCoverage,
  type FeedingMethod,
  type OnboardingAnswers,
  type SleepStyle,
} from '@/lib/needTemplates'
import { useToasts } from '@/lib/toast'

/**
 * OnboardingWizardScreen — the moat.
 *
 * After a family is bootstrapped, we ask three fast questions and use the
 * answers to seed 6-10 recurring Need cards so the parent lands on /needs
 * with their week pre-scaffolded. Every seed is editable/deletable; nothing
 * is destiny. The goal is to eliminate "empty app, now what?" — the moment
 * every competitor loses new parents.
 *
 * INV-4 (event immutability) preserved: each seeded need also emits a
 * `need.created` event with `source_type='wren_seed'` so the timeline
 * reflects the auto-fill honestly.
 */

const FEEDING_OPTIONS: Array<{ id: FeedingMethod; emoji: string; label: string; sub: string }> = [
  { id: 'breast',  emoji: '🤱', label: 'Breast',       sub: 'Nursing only' },
  { id: 'bottle',  emoji: '🍼', label: 'Bottle',       sub: 'Formula or expressed milk' },
  { id: 'mixed',   emoji: '🔀', label: 'Mixed',        sub: 'Both breast + bottle' },
  { id: 'solids',  emoji: '🥣', label: 'Solids too',   sub: '6mo+ eating food' },
]

const SLEEP_OPTIONS: Array<{ id: SleepStyle; emoji: string; label: string; sub: string }> = [
  { id: 'own_crib',   emoji: '🛏️', label: 'Own room, own crib', sub: 'Baby has their own room' },
  { id: 'room_share', emoji: '🏠', label: 'Same room, own crib', sub: 'Room-sharing (AAP recommends 6-12mo)' },
  { id: 'bedshare',   emoji: '💤', label: 'Bedsharing',          sub: 'Sleeping in the same bed' },
]

const CARE_OPTIONS: Array<{ id: CareCoverage; emoji: string; label: string; sub: string }> = [
  { id: 'me_home',       emoji: '🏡', label: "I'm home with baby", sub: 'Stay-at-home primary caregiver' },
  { id: 'partner_home',  emoji: '👥', label: 'Partner is home',    sub: 'Two-parent daytime coverage' },
  { id: 'daycare_mf',    emoji: '🚌', label: 'Daycare M-F',        sub: 'External childcare weekdays' },
  { id: 'nanny',         emoji: '👶', label: 'Nanny',              sub: 'Paid in-home caregiver' },
  { id: 'other',         emoji: '➕', label: 'Something else',     sub: 'Family, mixed schedule, etc.' },
]

export function OnboardingWizardScreen() {
  const nav = useNavigate()
  const familyId = useAppStore((s) => s.familyId)
  const user = useAppStore((s) => s.user)
  const focusedPerson = useFocusedPerson()
  const pushToast = useToasts((s) => s.push)

  const [feeding, setFeeding] = useState<FeedingMethod | null>(null)
  const [sleep, setSleep] = useState<SleepStyle | null>(null)
  const [care, setCare] = useState<CareCoverage | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const answers: OnboardingAnswers | null = feeding && sleep && care ? { feeding, sleep, care } : null

  const ageMonths = useMemo(() => {
    if (!focusedPerson?.date_of_birth) return null
    const dob = new Date(focusedPerson.date_of_birth)
    const now = new Date()
    return (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth())
  }, [focusedPerson?.date_of_birth])

  const preview = answers ? seedPreview(answers, ageMonths) : null

  async function fillMyWeek() {
    if (!answers || !familyId || !user) return
    setBusy(true); setErr(null)
    try {
      const seeds = seedNeedsFromAnswers(answers, ageMonths, focusedPerson?.id ?? null)
      const now = new Date()
      const rows = seeds.map((s) => ({
        family_id: familyId,
        person_id: s.personId ?? null,
        title: s.title,
        description: s.description ?? null,
        category: s.category,
        urgency: s.urgency,
        source_type: 'parent' as const,     // The parent authorized the seed via wizard consent.
        confidence: 1.0,
        due_at: seedDueAt(s, now),
        recurrence: s.recurrence,
        seeded_from: 'onboarding_wizard_v1',
        created_by_user_id: user.id,
      }))

      const { data, error } = await supabase.from('needs').insert(rows).select('id, title, category, urgency, recurrence, due_at')
      if (error) throw new Error(error.message)

      // Emit need.created events for each seeded need so timeline + cross-device sync see them.
      // Fire in sequence to preserve monotonic occurred_at ordering.
      for (const r of data ?? []) {
        await emitEvent({
          familyId,
          personId: focusedPerson?.id ?? null,
          userId: user.id,
          eventType: 'need.created',
          payload: {
            need_id: r.id,
            title: r.title,
            category: r.category,
            urgency: r.urgency,
            source_type: 'parent',
            confidence: 1.0,
            seeded_from: 'onboarding_wizard_v1',
            ...(r.recurrence ? { recurrence: r.recurrence } : {}),
            ...(r.due_at ? { due_at: r.due_at } : {}),
          } as import('@/lib/types').JSONValue,
        })
      }

      pushToast({
        title: 'Your week is scaffolded',
        body: `${data?.length ?? 0} recurring reminders added. Edit or delete anytime.`,
        tone: 'mint',
        ttl: 4000,
      })
      nav('/needs', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function skip() {
    nav('/', { replace: true })
  }

  return (
    <Screen title="One minute setup">
      <div className="px-5 pt-4 pb-8 space-y-7">
        <div>
          <h1 className="font-serif italic text-hero text-text-primary">
            Answer three questions.
          </h1>
          <p className="mt-2 text-body text-text-secondary">
            Wren will scaffold a week of routines for you — recurring reminders you can edit or delete.
            No other family record does this.
          </p>
        </div>

        <QuestionBlock number={1} title="How is baby fed?">
          {FEEDING_OPTIONS.map((o) => (
            <ChoiceButton key={o.id} emoji={o.emoji} label={o.label} sub={o.sub} selected={feeding === o.id} onClick={() => setFeeding(o.id)} />
          ))}
        </QuestionBlock>

        <QuestionBlock number={2} title="How does baby sleep?">
          {SLEEP_OPTIONS.map((o) => (
            <ChoiceButton key={o.id} emoji={o.emoji} label={o.label} sub={o.sub} selected={sleep === o.id} onClick={() => setSleep(o.id)} />
          ))}
        </QuestionBlock>

        <QuestionBlock number={3} title="Who's with baby during the day?">
          {CARE_OPTIONS.map((o) => (
            <ChoiceButton key={o.id} emoji={o.emoji} label={o.label} sub={o.sub} selected={care === o.id} onClick={() => setCare(o.id)} />
          ))}
        </QuestionBlock>

        {preview && (
          <div className="rounded-r-sm border p-4 space-y-2"
               style={{ background: 'rgba(64,221,175,0.08)', borderColor: 'rgba(64,221,175,0.4)' }}>
            <Kicker color={PACK_COLORS.early_care.light}>What Wren will add</Kicker>
            <div className="text-body text-text-primary font-medium">{preview.total} recurring reminders</div>
            <ul className="text-caption text-text-secondary space-y-1">
              {preview.daily > 0    && <li>· {preview.daily} that repeat every day</li>}
              {preview.weekdays > 0 && <li>· {preview.weekdays} Mon–Fri only</li>}
              {preview.weekly > 0   && <li>· {preview.weekly} once a week</li>}
              {preview.oneShot > 0  && <li>· {preview.oneShot} one-time reminder</li>}
            </ul>
          </div>
        )}

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}

        <div className="space-y-2 pt-2">
          <Button
            variant="primary"
            size="lg"
            packColor={PACK_COLORS.early_care.light}
            loading={busy}
            disabled={!answers}
            onClick={fillMyWeek}
            className="w-full"
          >
            {answers ? 'Fill my week' : 'Answer all three'}
          </Button>
          <Button variant="secondary" size="lg" onClick={skip} disabled={busy} className="w-full">
            Skip for now
          </Button>
        </div>
      </div>
    </Screen>
  )
}

function QuestionBlock({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-caption text-text-tertiary font-mono">{number}</span>
        <h2 className="text-title font-display text-text-primary">{title}</h2>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2">
        {children}
      </div>
    </div>
  )
}

function ChoiceButton({ emoji, label, sub, selected, onClick }: { emoji: string; label: string; sub: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-r-sm p-3 flex items-center gap-3 transition-all border"
      style={{
        background: selected ? 'rgba(64,221,175,0.12)' : 'rgb(var(--bg-elevated))',
        borderColor: selected ? 'rgba(64,221,175,0.5)' : 'rgb(var(--divider-subtle))',
      }}
      aria-pressed={selected}
    >
      <span className="text-2xl leading-none" aria-hidden>{emoji}</span>
      <div className="flex-1 min-w-0">
        <div className="text-body text-text-primary font-medium">{label}</div>
        <div className="text-caption text-text-tertiary">{sub}</div>
      </div>
      {selected && (
        <span className="h-6 w-6 rounded-full flex items-center justify-center text-white text-caption shrink-0"
              style={{ background: 'linear-gradient(180deg, #40DDAF 0%, #2ED2A3 100%)' }}>
          ✓
        </span>
      )}
    </button>
  )
}
